import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MinusIcon, PlusIcon, RefreshCwIcon, ChevronRightIcon, SaveIcon, RotateCcwIcon } from 'lucide-react';
import PageHeader from '../components/PageHeader';
interface SettingsPageProps {
  fontSize: number;
  setFontSize: (size: number) => void;
  inSyncMode?: boolean;
  highlightColor?: string;
  setHighlightColor?: (color: string) => void;
}
const SettingsPage: React.FC<SettingsPageProps> = ({
  fontSize,
  setFontSize,
  inSyncMode = false,
  highlightColor = 'rgba(2, 25, 128, 0.2)',
  setHighlightColor = () => {}
}) => {
  const navigate = useNavigate();
  const [mirrorTextHorizontal, setMirrorTextHorizontal] = useState(false);
  const [mirrorTextVertical, setMirrorTextVertical] = useState(false);
  const [capitalizeText, setCapitalizeText] = useState(false);
  const [textColor, setTextColor] = useState('#51a7fa');
  const [localHighlightColor, setLocalHighlightColor] = useState(highlightColor);
  const [highlightHeight, setHighlightHeight] = useState(33);
  const [backgroundColor, setBackgroundColor] = useState('#000000');
  const [sideMargin, setSideMargin] = useState(3);
  // Sync local state with prop when prop changes
  useEffect(() => {
    setLocalHighlightColor(highlightColor);
  }, [highlightColor]);
  const decreaseFontSize = () => {
    if (fontSize > 12) {
      setFontSize(fontSize - 2);
    }
  };
  const increaseFontSize = () => {
    setFontSize(fontSize + 2);
  };
  const toggleMirrorTextHorizontal = () => {
    setMirrorTextHorizontal(!mirrorTextHorizontal);
  };
  const toggleMirrorTextVertical = () => {
    setMirrorTextVertical(!mirrorTextVertical);
  };
  const toggleCapitalizeText = () => {
    setCapitalizeText(!capitalizeText);
  };
  const decreaseSideMargin = () => {
    if (sideMargin > 1) {
      setSideMargin(sideMargin - 1);
    }
  };
  const increaseSideMargin = () => {
    if (sideMargin < 5) {
      setSideMargin(sideMargin + 1);
    }
  };
  const decreaseHighlightHeight = () => {
    if (highlightHeight > 10) {
      setHighlightHeight(highlightHeight - 5);
    }
  };
  const increaseHighlightHeight = () => {
    if (highlightHeight < 50) {
      setHighlightHeight(highlightHeight + 5);
    }
  };
  const handleBackNavigation = () => {
    if (inSyncMode) {
      // Navigate back to the demo sync prompter page
      navigate('/demo-sync');
    } else {
      // Navigate back to the main prompter page
      navigate('/');
    }
  };
  const saveSettings = () => {
    // Update the parent component's highlight color
    setHighlightColor(localHighlightColor);
    // In a real app, this would save settings to local storage or a database
    alert('Settings saved!');
  };
  const resetSettings = () => {
    // In a real app, this would reset settings to defaults
    if (window.confirm('Reset all settings to defaults?')) {
      setTextColor('#51a7fa');
      setBackgroundColor('#000000');
      setLocalHighlightColor('rgba(2, 25, 128, 0.2)');
      setHighlightColor('rgba(2, 25, 128, 0.2)');
      setHighlightHeight(33);
      setSideMargin(3);
      setFontSize(24);
      setMirrorTextHorizontal(false);
      setMirrorTextVertical(false);
      setCapitalizeText(false);
    }
  };
  // Extract color and opacity from rgba string
  const extractColorInfo = (rgba: string) => {
    const matches = rgba.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/);
    if (matches) {
      const [_, r, g, b, a] = matches;
      return {
        hex: rgbToHex(parseInt(r), parseInt(g), parseInt(b)),
        opacity: parseFloat(a) * 100
      };
    }
    return {
      hex: '#021980',
      opacity: 20
    };
  };
  const colorInfo = extractColorInfo(localHighlightColor);
  return <div className="flex-1 flex flex-col bg-black" data-id="element-243">
      <PageHeader title="Settings" leftContent={<button onClick={handleBackNavigation} className="text-[#51a7fa] hover:text-white" data-id="element-245">
            <ChevronRightIcon className="rotate-180" size={24} data-id="element-246" />
          </button>} rightContent={<div className="flex space-x-4" data-id="element-247">
            <button onClick={saveSettings} className="text-[#51a7fa] hover:text-white" data-id="element-248">
              <SaveIcon size={24} data-id="element-249" />
            </button>
            <button onClick={resetSettings} className="text-[#51a7fa] hover:text-white" data-id="element-250">
              <RotateCcwIcon size={24} data-id="element-251" />
            </button>
          </div>} showBackButton={false} data-id="element-244" />
      <div className="flex-1 p-6 pt-20 pb-20 overflow-y-auto bg-black" data-id="element-252">
        <div className="space-y-4" data-id="element-253">
          {/* Sync Option - Only show if not in sync mode */}
          {!inSyncMode && <div className="bg-gray-900 p-4 rounded-lg cursor-pointer hover:bg-gray-800" onClick={() => navigate('/sync')} data-id="element-254">
              <div className="flex items-center justify-between" data-id="element-255">
                <div className="flex items-center" data-id="element-256">
                  <RefreshCwIcon size={24} className="mr-3 text-[#51a7fa]" data-id="element-257" />
                  <div data-id="element-258">
                    <h3 className="text-lg font-medium" data-id="element-259">Sync</h3>
                    <p className="text-sm text-gray-400" data-id="element-260">
                      Connect multiple devices
                    </p>
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-500" data-id="element-261" />
              </div>
            </div>}
          {/* Font Size Controls */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-262">
            <h3 className="text-lg font-medium mb-3" data-id="element-263">Font Size</h3>
            <div className="flex items-center justify-between" data-id="element-264">
              <button onClick={decreaseFontSize} className="bg-gray-800 text-white p-3 rounded-lg" data-id="element-265">
                <MinusIcon size={24} data-id="element-266" />
              </button>
              <div className="flex-1 flex flex-col items-center" data-id="element-267">
                <div className="text-2xl font-bold" data-id="element-268">{fontSize}</div>
              </div>
              <button onClick={increaseFontSize} className="bg-gray-800 text-white p-3 rounded-lg" data-id="element-269">
                <PlusIcon size={24} data-id="element-270" />
              </button>
            </div>
          </div>
          {/* Text Color */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-271">
            <h3 className="text-lg font-medium mb-3" data-id="element-272">Text Color</h3>
            <div className="flex items-center" data-id="element-273">
              <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-12 h-12 rounded-lg mr-4 cursor-pointer" data-id="element-274" />
              <div className="flex-1" data-id="element-275">
                <p className="text-sm text-gray-400" data-id="element-276">
                  Choose the color of the teleprompter text
                </p>
              </div>
            </div>
          </div>
          {/* Background Color */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-277">
            <h3 className="text-lg font-medium mb-3" data-id="element-278">Background Color</h3>
            <div className="flex items-center" data-id="element-279">
              <input type="color" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} className="w-12 h-12 rounded-lg mr-4 cursor-pointer" data-id="element-280" />
              <div className="flex-1" data-id="element-281">
                <p className="text-sm text-gray-400" data-id="element-282">
                  Choose the background color of the teleprompter
                </p>
              </div>
            </div>
          </div>
          {/* Highlight Strip Color */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-283">
            <h3 className="text-lg font-medium mb-3" data-id="element-284">Highlight Strip</h3>
            {/* Color Picker */}
            <div className="flex items-center mb-4" data-id="element-285">
              <input type="color" value={colorInfo.hex} onChange={e => {
              const rgb = hexToRgb(e.target.value);
              const opacity = colorInfo.opacity / 100;
              setLocalHighlightColor(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`);
            }} className="w-12 h-12 rounded-lg mr-4 cursor-pointer" data-id="element-286" />
              <div className="flex-1" data-id="element-287">
                <p className="text-sm text-gray-400" data-id="element-288">
                  Choose the highlight strip color
                </p>
              </div>
            </div>
            {/* Opacity Slider */}
            <div className="mb-4" data-id="element-289">
              <label className="block text-sm text-gray-400 mb-2" data-id="element-290">
                Opacity: {Math.round(colorInfo.opacity)}%
              </label>
              <input type="range" min="0" max="100" value={colorInfo.opacity} onChange={e => {
              const rgb = hexToRgb(colorInfo.hex);
              const opacity = parseInt(e.target.value) / 100;
              setLocalHighlightColor(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`);
            }} className="w-full" data-id="element-291" />
            </div>
            {/* Height Control */}
            <h4 className="text-sm font-medium mb-2" data-id="element-292">
              Height ({highlightHeight}%)
            </h4>
            <div className="flex items-center justify-between" data-id="element-293">
              <button onClick={decreaseHighlightHeight} className="bg-gray-800 text-white p-2 rounded-lg" data-id="element-294">
                <MinusIcon size={20} data-id="element-295" />
              </button>
              <div className="flex-1 mx-4" data-id="element-296">
                <input type="range" min="10" max="50" value={highlightHeight} onChange={e => setHighlightHeight(parseInt(e.target.value))} className="w-full" data-id="element-297" />
              </div>
              <button onClick={increaseHighlightHeight} className="bg-gray-800 text-white p-2 rounded-lg" data-id="element-298">
                <PlusIcon size={20} data-id="element-299" />
              </button>
            </div>
          </div>
          {/* Side Margins */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-300">
            <h3 className="text-lg font-medium mb-3" data-id="element-301">Side Margins</h3>
            <div className="flex items-center justify-between" data-id="element-302">
              <button onClick={decreaseSideMargin} className="bg-gray-800 text-white p-3 rounded-lg" data-id="element-303">
                <MinusIcon size={24} data-id="element-304" />
              </button>
              <div className="flex-1 flex flex-col items-center" data-id="element-305">
                <div className="text-2xl font-bold" data-id="element-306">{sideMargin}</div>
                <div className="text-xs text-gray-400" data-id="element-307">
                  1 (Narrow) - 5 (Wide)
                </div>
              </div>
              <button onClick={increaseSideMargin} className="bg-gray-800 text-white p-3 rounded-lg" data-id="element-308">
                <PlusIcon size={24} data-id="element-309" />
              </button>
            </div>
          </div>
          {/* Mirror Text Horizontal Option */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-310">
            <div className="flex items-center justify-between" data-id="element-311">
              <div data-id="element-312">
                <h3 className="text-lg font-medium" data-id="element-313">
                  Mirror Text Horizontally
                </h3>
                <p className="text-sm text-gray-400" data-id="element-314">Flip text horizontally</p>
              </div>
              <div className="relative inline-block w-12 align-middle select-none" data-id="element-315">
                <input type="checkbox" name="toggle-horizontal" id="toggle-horizontal" className="opacity-0 absolute w-0 h-0" checked={mirrorTextHorizontal} onChange={toggleMirrorTextHorizontal} data-id="element-316" />
                <label htmlFor="toggle-horizontal" className={`block overflow-hidden h-6 rounded-full bg-gray-700 cursor-pointer ${mirrorTextHorizontal ? 'bg-blue-600' : ''}`} data-id="element-317">
                  <span className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in ${mirrorTextHorizontal ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-318"></span>
                </label>
              </div>
            </div>
          </div>
          {/* Mirror Text Vertical Option */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-319">
            <div className="flex items-center justify-between" data-id="element-320">
              <div data-id="element-321">
                <h3 className="text-lg font-medium" data-id="element-322">Mirror Text Vertically</h3>
                <p className="text-sm text-gray-400" data-id="element-323">Flip text vertically</p>
              </div>
              <div className="relative inline-block w-12 align-middle select-none" data-id="element-324">
                <input type="checkbox" name="toggle-vertical" id="toggle-vertical" className="opacity-0 absolute w-0 h-0" checked={mirrorTextVertical} onChange={toggleMirrorTextVertical} data-id="element-325" />
                <label htmlFor="toggle-vertical" className={`block overflow-hidden h-6 rounded-full bg-gray-700 cursor-pointer ${mirrorTextVertical ? 'bg-blue-600' : ''}`} data-id="element-326">
                  <span className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in ${mirrorTextVertical ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-327"></span>
                </label>
              </div>
            </div>
          </div>
          {/* Capitalize Text Option */}
          <div className="bg-gray-900 p-4 rounded-lg" data-id="element-328">
            <div className="flex items-center justify-between" data-id="element-329">
              <div data-id="element-330">
                <h3 className="text-lg font-medium" data-id="element-331">Capitalize Text</h3>
                <p className="text-sm text-gray-400" data-id="element-332">
                  Convert all text to uppercase
                </p>
              </div>
              <div className="relative inline-block w-12 align-middle select-none" data-id="element-333">
                <input type="checkbox" name="toggle-capitalize" id="toggle-capitalize" className="opacity-0 absolute w-0 h-0" checked={capitalizeText} onChange={toggleCapitalizeText} data-id="element-334" />
                <label htmlFor="toggle-capitalize" className={`block overflow-hidden h-6 rounded-full bg-gray-700 cursor-pointer ${capitalizeText ? 'bg-blue-600' : ''}`} data-id="element-335">
                  <span className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in ${capitalizeText ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-336"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
// Helper function to convert hex to rgb
function hexToRgb(hex: string) {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  // Parse the hex values
  const bigint = parseInt(hex, 16);
  const r = bigint >> 16 & 255;
  const g = bigint >> 8 & 255;
  const b = bigint & 255;
  return {
    r,
    g,
    b
  };
}
// Helper function to convert rgb to hex
function rgbToHex(r: number, g: number, b: number) {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}
export default SettingsPage;